import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import {
  SUPER_AFFILIATE_CODE_TYPES,
  checkPromoCodeAvailability,
  createSuperAffiliatePromoCode,
} from "../../../../store/reducers/affiliateReducer";

const CODE_LABELS = {
  one_time_50: "One-Time 50% Code",
  normal: "Normal Promotion Code",
};

const CODE_HINTS = {
  one_time_50:
    "Available once only. Must end in 50 - the rest is your choice (e.g. LK50).",
  normal:
    "Your everyday code. During a sale it matches the best available offer.",
};

const MIN_CODE_LENGTH = 2;

const DEFAULT_RULES = {
  one_time_50: {
    discount_percent: 50,
    commission_percent: 10,
    required_suffix: "50",
    min_length: 2,
    max_length: 16,
    example: "LK50",
  },
  normal: {
    discount_percent: 10,
    commission_percent: 10,
    required_suffix: null,
    min_length: 2,
    max_length: 16,
    example: "LK",
  },
};

// A claimed code comes back as an object - { id, code, code_type,
// discount_percent, commission_percent, url, is_active, created } - not the bare
// string this used to assume. Rendering the object itself is React error #31.
const codeValue = (entry) =>
  typeof entry === "string" ? entry : entry?.code || "";

// promo_codes is keyed by rule name, but tolerate an array keyed by code_type.
const promoCodeEntry = (superAffiliate, codeKey) => {
  const codes = superAffiliate?.promo_codes;
  if (!codes) return null;
  if (Array.isArray(codes))
    return (
      codes.find(
        (entry) => entry?.code_type === SUPER_AFFILIATE_CODE_TYPES[codeKey]
      ) || null
    );
  return codes[codeKey] ?? null;
};

// The claimed code carries its own terms; fall back to the advertised rules.
const codeTerms = (entry, rules) => ({
  discount: entry?.discount_percent ?? rules?.discount_percent,
  commission: entry?.commission_percent ?? rules?.commission_percent,
});

// Server rules win on the terms, but not on the minimum length (floored at
// MIN_CODE_LENGTH) or the example code, which stays our own copy.
const rulesFor = (superAffiliate, codeKey) => {
  const defaults = DEFAULT_RULES[codeKey];
  return {
    ...defaults,
    ...superAffiliate?.promo_code_rules?.[codeKey],
    min_length: MIN_CODE_LENGTH,
    example: defaults?.example || "LK",
  };
};

export const codeKeysFor = (superAffiliate) => {
  const rules = superAffiliate?.promo_code_rules;
  return rules && Object.keys(rules).length
    ? Object.keys(rules)
    : Object.keys(DEFAULT_RULES);
};

// Mirrors the backend rules so obvious mistakes cost no request.
const validateCode = (code, rules) => {
  const value = String(code || "").trim();
  if (!value) return "Enter a code";
  if (!/^[A-Z0-9]+$/.test(value))
    return "Use letters and numbers only, no spaces";
  if (rules?.min_length && value.length < rules.min_length)
    return `Minimum ${rules.min_length} characters`;
  if (rules?.max_length && value.length > rules.max_length)
    return `Maximum ${rules.max_length} characters`;
  if (rules?.required_suffix && !value.endsWith(rules.required_suffix))
    return `Must end with ${rules.required_suffix}`;
  return null;
};

/* ---------------------- Claimed codes, shown on dashboard ---------------------- */

export const PromoCodeList = ({ superAffiliate }) => {
  const claimed = codeKeysFor(superAffiliate)
    .map((key) => ({ key, entry: promoCodeEntry(superAffiliate, key) }))
    .filter(({ entry }) => codeValue(entry));

  // Legacy Super Affiliates have no claimed codes and are never prompted, so
  // there is nothing to show them here.
  if (!claimed.length) return null;

  return (
    <div className="sa-promo">
      <div className="sa-promo-head">
        <h4>Your Super Affiliate Promo Codes</h4>
      </div>
      {claimed.map(({ key, entry }) => {
        const terms = codeTerms(entry, rulesFor(superAffiliate, key));
        return (
          <div className="sa-code-row saved" key={key}>
            <div className="sa-code-meta">
              <span className="sa-code-label">{CODE_LABELS[key] || key}</span>
              <span className="sa-code-terms">
                {terms.discount}% off &middot; {terms.commission}% commission
              </span>
            </div>
            <div className="sa-code-value">{codeValue(entry)}</div>
          </div>
        );
      })}
    </div>
  );
};

/* ------------------------------- Setup pop-up ------------------------------- */

const CodeRow = ({ codeKey, rules, claimedCode, onClaimed }) => {
  const dispatch = useDispatch();
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  // null = not checked yet, otherwise { usable, message }
  const [availability, setAvailability] = useState(null);
  const [saving, setSaving] = useState(false);

  const validationError = useMemo(
    () => (code ? validateCode(code, rules) : null),
    [code, rules]
  );

  // Editing the code invalidates the previous availability answer.
  useEffect(() => {
    setAvailability(null);
  }, [code]);

  const claimedValue = codeValue(claimedCode);
  if (claimedValue) {
    const terms = codeTerms(claimedCode, rules);
    return (
      <div className="sa-code-row saved">
        <div className="sa-code-meta">
          <span className="sa-code-label">{CODE_LABELS[codeKey]}</span>
          <span className="sa-code-terms">
            {terms.discount}% off &middot; {terms.commission}% commission
          </span>
        </div>
        <div className="sa-code-value">{claimedValue}</div>
      </div>
    );
  }

  const handleCheck = async () => {
    if (checking || validationError) return;
    setChecking(true);
    try {
      const result = await dispatch(
        checkPromoCodeAvailability(code, SUPER_AFFILIATE_CODE_TYPES[codeKey])
      );
      // The endpoint always answers 200 - both flags must pass.
      const usable = Boolean(result?.valid) && Boolean(result?.available);
      setAvailability({
        usable,
        message:
          result?.message ||
          result?.detail ||
          (usable
            ? "Code is available"
            : result?.valid === false
            ? "That code does not match the required format"
            : "That code is already taken"),
      });
    } catch (error) {
      setAvailability({
        usable: false,
        message:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Could not check that code. Please try again.",
      });
    } finally {
      setChecking(false);
    }
  };

  const handleClaim = async () => {
    if (saving || validationError || !availability?.usable) return;
    setSaving(true);
    try {
      const response = await dispatch(
        createSuperAffiliatePromoCode({
          code_type: SUPER_AFFILIATE_CODE_TYPES[codeKey],
          code,
        })
      );
      toast.success(`${CODE_LABELS[codeKey]} claimed.`);
      onClaimed?.(codeKey, codeValue(response) || code);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Could not claim that code. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sa-code-row">
      <div className="sa-code-meta">
        <span className="sa-code-label">{CODE_LABELS[codeKey]}</span>
        <span className="sa-code-terms">
          {rules?.discount_percent}% off &middot; {rules?.commission_percent}%
          commission
        </span>
        <span className="sa-code-hint">{CODE_HINTS[codeKey]}</span>
      </div>

      <div className="sa-code-form">
        <input
          type="text"
          value={code}
          placeholder={`e.g. ${rules?.example || "LK"}`}
          maxLength={rules?.max_length || 16}
          // Codes are stored uppercase; keep the field consistent.
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          className={validationError && code ? "has-error" : ""}
          aria-label={`${CODE_LABELS[codeKey]} code`}
        />
        <button
          type="button"
          className="sa-btn sa-btn-ghost"
          onClick={handleCheck}
          disabled={checking || !code || Boolean(validationError)}
        >
          {checking ? "Checking..." : "Check"}
        </button>
        <button
          type="button"
          className="sa-btn"
          onClick={handleClaim}
          disabled={saving || !availability?.usable}
        >
          {saving ? "Claiming..." : "Claim"}
        </button>
      </div>

      {code && validationError && (
        <small className="sa-error">{validationError}</small>
      )}
      {!validationError && availability && (
        <small className={availability.usable ? "sa-ok" : "sa-error"}>
          {availability.message}
        </small>
      )}
    </div>
  );
};

const PromoCodeSetupModal = ({ open, onClose, superAffiliate, onClaimed }) => {
  // Codes claimed in this session, before the dashboard refetch lands.
  const [localCodes, setLocalCodes] = useState({});

  if (!open) return null;

  const keys = codeKeysFor(superAffiliate);
  const claimedFor = (key) =>
    promoCodeEntry(superAffiliate, key) || localCodes[key] || null;
  const allClaimed = keys.every((key) => codeValue(claimedFor(key)));

  const handleClaimed = (codeKey, value) => {
    setLocalCodes((prev) => ({ ...prev, [codeKey]: value }));
    onClaimed?.();
  };

  return (
    <div className="sa-modal-overlay" role="presentation">
      <div
        className="sa-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sa-promo-title"
      >
        <div className="sa-modal-head">
          <h3 id="sa-promo-title">Set Up Your Promo Codes</h3>
          <button
            type="button"
            className="sa-close"
            onClick={() => onClose?.()}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="sa-modal-body sa-modal-body-stack">
          <p className="sa-modal-intro">
            You have been approved as a Super Affiliate. Choose your two codes -
            each one can only be claimed once.
          </p>

          {keys.map((codeKey) => (
            <CodeRow
              key={codeKey}
              codeKey={codeKey}
              rules={rulesFor(superAffiliate, codeKey)}
              claimedCode={claimedFor(codeKey)}
              onClaimed={handleClaimed}
            />
          ))}

          <div className="sa-modal-actions">
            <button
              type="button"
              className={allClaimed ? "sa-btn" : "sa-btn sa-btn-ghost"}
              onClick={() => onClose?.()}
            >
              {allClaimed ? "Done" : "I'll do this later"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoCodeSetupModal;
